/**
 * Template Data Transformation Utilities
 * Functions for converting frontend template data to Lambda-compatible format
 */

/**
 * Transforms frontend template data to Lambda-compatible format
 * @param {Object} frontendData - Template data from frontend form
 * @returns {Object} Lambda-compatible template data
 */
export const transformTemplateForLambda = (frontendData) => {
  // Extract required fields with defaults
  const template_id = frontendData.id || "";
  const name = frontendData.name || "";
  const version = parseInt(frontendData.version) || 1;
  const country =
    frontendData.metadata?.country || frontendData.country || "IT"; // Default to Italy
  const createdBy = frontendData.createdBy || "unknown_user";

  // Extract template content fields
  const description = frontendData.description || "";
  const isActive =
    frontendData.isActive !== undefined ? frontendData.isActive : true;
  const items = frontendData.items || [];

  // Validate required fields
  if (!name.trim()) {
    throw new Error("Template name is required");
  }

  // Transform to Lambda expected format
  const lambdaData = {
    id: template_id,
    name: name.trim(),
    version: version,
    country: country,
    createdBy: createdBy,
    templateContent: {
      name: name.trim(),
      version: version,
      country: country,
      description: description,
      isActive: isActive,
      items: items.map(transformItemForLambda),
    },
  };

  return lambdaData;
};

/**
 * Transforms a frontend item to Lambda-compatible format
 * @param {Object} item - Frontend item object
 * @returns {Object} Lambda-compatible item
 */
export const transformItemForLambda = (item) => {
  return {
    id:
      item.id ||
      `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type: item.type || "checkbox",
    description: item.description,
    defaultValue: item.defaultValue || {},
    // defaultData: item.defaultData || {},
    label: item.label || "",
    required: Boolean(item.required),
    order: item.order !== undefined ? item.order : 0,
    config: item.config || {},
  };
};

/**
 * Transforms Lambda response back to frontend format
 * @param {Object} lambdaResponse - Response from Lambda function
 * @returns {Object} Frontend-compatible template data
 */
export const transformLambdaResponseToFrontend = (lambdaResponse) => {
  if (!lambdaResponse || !lambdaResponse.template) {
    throw new Error("Invalid Lambda response format");
  }

  const lambdaTemplate = lambdaResponse.template;

  // Transform back to frontend format
  const frontendTemplate = {
    id: lambdaTemplate.id,
    name: lambdaTemplate.name,
    description: "", // Will be in templateContent when we fetch full template
    version: lambdaTemplate.version,
    isActive: true, // Default, will be updated when we fetch full template
    items: [], // Will be populated when we fetch full template content
    metadata: {
      estimatedTimeMinutes: 15,
      category: "general",
      tags: [],
      countries: [lambdaTemplate.country],
      languages: [],
    },
    createdBy: lambdaTemplate.createdBy,
    createdAt: lambdaTemplate.createdAt,
    updatedAt: new Date().toISOString(),
  };

  return frontendTemplate;
};

/**
 * Validates frontend template data before transformation
 * @param {Object} templateData - Frontend template data
 * @returns {Object} Validation result { isValid: boolean, errors: string[] }
 */
export const validateTemplateForLambda = (templateData) => {
  const errors = [];

  // Required field validation
  if (!templateData.name || !templateData.name.trim()) {
    errors.push("Template name is required");
  }

  // Version should be an integer
  if (!templateData.version || isNaN(templateData.version)) {
    errors.push("Template version is required");
  }

  if (!templateData.createdBy || !templateData.createdBy.trim()) {
    errors.push("Created by field is required");
  }

  // Items validation
  if (!Array.isArray(templateData.items)) {
    errors.push("Template items must be an array");
  } else if (templateData.items.length === 0) {
    errors.push("Template must have at least one item");
  } else {
    // Validate each item
    templateData.items.forEach((item, index) => {
      if (!item.label || !item.label.trim()) {
        errors.push(`Item ${index + 1}: Label is required`);
      }
      if (!item.type || !item.type.trim()) {
        errors.push(`Item ${index + 1}: Type is required`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export default {
  transformTemplateForLambda,
  transformItemForLambda,
  transformLambdaResponseToFrontend,
  validateTemplateForLambda,
};
